import React, { useState } from 'react';
import Modal from '../Modal/Modal';
import { Shield, CreditCard, KeyRound, Ban, AlertTriangle, Receipt, ShieldAlert, Scale, FileEdit, BookOpen, Mail, CheckCircle } from 'lucide-react';
import './PayPolicy.scss';

const PayPolicy = ({ open, onClose, triggerText = 'View Terms and Conditions' }) => {
  const [localOpen, setLocalOpen] = useState(false);
  const isControlled = typeof open !== 'undefined';
  const isOpen = isControlled ? open : localOpen;

  const handleOpen = () => {
    if (!isControlled) setLocalOpen(true);
    else if (typeof onClose === 'function') onClose(false); // for controlled components, parent should handle
  };

  const handleClose = () => {
    if (isControlled) {
      if (typeof onClose === 'function') onClose();
    } else setLocalOpen(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Payment Terms and Conditions"
      size="medium"
      
    >
      <div className="pay-policy" role="document" aria-label="Payment Terms and Conditions">
        <section className="pay-policy__intro">
          <h3>
            <Shield size={18} className="intro-icon" />
            Payment Terms and Conditions (Razorpay Integrated Payments)
          </h3>
          <p>These Payment Terms govern all payments made on the Platform.</p>
        </section>

        <section className="pay-policy__section pay-policy__section--highlight">
          <h3>
            <CreditCard size={16} className="section-icon" />
            1. Payment Gateway
          </h3>
          <p>All payments are processed through Razorpay, a third-party payment gateway. The Platform does not store your card, UPI, or bank details.</p>
        </section>

        <section className="pay-policy__section">
          <h3>
            <KeyRound size={16} className="section-icon" />
            2. Free Trial, Subscription & Access
          </h3>
          <p>New accounts receive a <strong>15-day free trial</strong> with no card required (Google sign-in supported). Trial expiry suspends paid features until a paid plan is activated.</p>
          <p>Paid subscriptions (Silver / Gold) are typically valid for <strong>one full year</strong> from activation unless a limited signup offer states a different duration. Admins may grant extra days or promotional offers. Access to paid services is granted only after successful payment confirmation. Plan status, expiry, and remaining days are shown in the app; expired plans restrict paid features until renewal or reactivation.</p>
        </section>

        <section className="pay-policy__section pay-policy__section--danger">
          <h3>
            <Ban size={16} className="section-icon" />
            3. No Refund Policy
          </h3>
          <p>All payments made on the Platform are <strong>NON-REFUNDABLE</strong>. There is no refund, cancellation, or chargeback facility, under any circumstances. This includes: partial usage, non-usage, accidental payments, trial expiry, plan downgrades, or account suspension or termination. If required by applicable law, exceptions will be handled as required.</p>
        </section>

        <section className="pay-policy__section">
          <h3>
            <AlertTriangle size={16} className="section-icon" />
            4. Payment Failures, Expiry & Reactivation
          </h3>
          <p>In case of payment failure, access will not be granted. The Platform is not responsible for bank issues, Razorpay downtime, network failures, or delayed confirmations. Failed or expired payments may be retried; order and payment IDs are retained for reconciliation. Expired subscriptions can be reactivated by purchasing a new subscription at the then-current price.</p>
        </section>

        <section className="pay-policy__section">
          <h3>
            <Receipt size={16} className="section-icon" />
            5. Taxes, Charges & Price Changes
          </h3>
          <p>Any applicable taxes, gateway fees, or charges are borne by the user. Pricing displayed may change without prior notice. Price changes apply only on renewal, upgrade, downgrade, reactivation, or new purchase — never mid-subscription. Invoices/receipts show the amount paid, plan, and transaction references.</p>
        </section>

        <section className="pay-policy__section">
          <h3>
            <ShieldAlert size={16} className="section-icon" />
            6. Fraud & Misuse
          </h3>
          <p>Any attempt at fraudulent payment activity will result in immediate account suspension. The Platform reserves the right to report fraudulent activity to relevant authorities.</p>
        </section>

        <section className="pay-policy__section">
          <h3>
            <Scale size={16} className="section-icon" />
            7. Disputes
          </h3>
          <p>Payment disputes must be raised directly with Razorpay or the respective bank. The Platform owner is not liable for third-party gateway disputes.</p>
        </section>

        <section className="pay-policy__section">
          <h3>
            <FileEdit size={16} className="section-icon" />
            8. Modification of Payment Terms
          </h3>
          <p>Payment terms may be updated at any time. Continued use of paid services implies acceptance of revised terms.</p>
        </section>

        <section className="pay-policy__section">
          <h3>
            <BookOpen size={16} className="section-icon" />
            9. Governing Law
          </h3>
          <p>These Payment Terms shall be governed by the laws of India.</p>
        </section>

        <section className="pay-policy__section pay-policy__section--contact">
          <h3>
            <Mail size={16} className="section-icon" />
            10. Contact Information
          </h3>
          <p>For payment-related queries, please contact our support team.</p>
          <div className="pay-policy__contact-wrap">
            <button
              type="button"
              className="pay-policy__contact"
              aria-label="Contact support"
              onClick={() => {
                handleClose();
                try {
                  window.dispatchEvent(new CustomEvent('openHelp'));
                } catch (e) {
                  console.error('Failed to dispatch openHelp event', e);
                }
              }}
            >
              <Mail size={14} />
              miventsite@gmail.com
            </button>
          </div>
        </section>

        <footer className="pay-policy__footer">
          <button
            type="button"
            className="btn-primary"
            onClick={handleClose}
          >
            <CheckCircle size={18} />
            I Agree
          </button>
        </footer>
      </div>
    </Modal>
  );
};

export default PayPolicy;
